import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CheckinTemplate, CheckinTemplateTimeMode, CheckinTemplateUpdate } from '../../api/client'
import { AppliesPrecision, formatClock, minutesToAppliesAt } from '../../utils/dates'
import CheckinTimePicker from '../CheckinTimePicker'

/**
 * What one shortcut does, reached by holding its pill.
 *
 * The hold used to go straight to a delete confirmation — one gesture, one
 * destination, and that destination destructive. It now opens this instead and
 * delete lives *inside*, so the gesture gained somewhere to go without losing
 * where it went. Delete still routes through the page's existing
 * `ConfirmationModal` and its existing copy; this component only raises
 * `onDelete`, because the confirmation and the request that follows it belong
 * to whoever owns the template list.
 *
 * The single setting is `time_mode` — what time the check-ins this shortcut
 * makes get. Three radios rather than a select: there are three of them
 * permanently, and a select hides two choices behind a tap on a row that is
 * already a phone-width row of pills.
 *
 * **The second line under an option is the time it would give, not a sentence
 * about it.** Each option first carried a line of explanation, which said in
 * words what the label already said and left the one thing a reader cannot
 * work out — *which* time `same` means — unsaid. So `same` shows the
 * template's own stored time and how precise it is, `current` shows the clock as
 * it reads right now and the `exactly` a clock reading earns, and "only date"
 * shows nothing, because a mode that produces no time has no time to show.
 *
 * **While `same` is selected, its line is the time itself, editable** — the
 * shared `CheckinTimePicker`, the same control the composer and the
 * time-assignment dialog use, so a shortcut's time is set the way every other
 * time is (a clock time is `exact`, a word `approximate`). It is how a shortcut
 * saved from an untimed check-in ever gets a time. Under the other two modes
 * the stored time does nothing, so it stays a read-only line rather than a
 * control that would look like it mattered.
 *
 * The body is not editable here; a shortcut's words stay write-once. Neither a
 * mode nor a time changes check-ins already made — those are ordinary rows the
 * moment they are written.
 */

interface TimeModeOption {
    mode: CheckinTemplateTimeMode
    label: string
}

/**
 * The human's own words for the three modes, in the order a routine gets less
 * specific: a fixed time, then whenever you tapped, then none.
 */
const TIME_MODE_OPTIONS: TimeModeOption[] = [
    { mode: 'same', label: 'Same time always' },
    { mode: 'current', label: 'Use current time' },
    { mode: 'none', label: 'Only date, no time' },
]

/** What a shortcut with no stored time has to show under `same`: nothing to
 *  reproduce, so the mode produces untimed check-ins. */
const NO_STORED_TIME = 'No time saved'

/**
 * How precise a time is, in words a reader who has never seen the column can
 * use. `exactly` / `roughly` are the pair the timestamp display setting already
 * says (`TIMESTAMP_DISPLAY_LABELS`), so two of the three are words this product
 * has taught already; `estimated` is what `inferred` means to someone who did
 * not choose the time — a drag or an accepted suggestion worked it out.
 */
const PRECISION_WORDS: Record<Exclude<AppliesPrecision, 'none'>, string> = {
    exact: 'exactly',
    approximate: 'roughly',
    inferred: 'estimated',
}

/**
 * A time and how precise it is, as one line — `9:00am · roughly`.
 *
 * `null` when there is no time at all, which callers render as their own kind
 * of nothing.
 */
function formatTimeWithPrecision(
    appliesAt: string | null | undefined,
    precision: AppliesPrecision | null | undefined,
): string | null {
    const clock = formatClock(appliesAt)
    if (clock === null) return null
    const word = precision && precision !== 'none' ? PRECISION_WORDS[precision] : null
    return word ? `${clock} · ${word}` : clock
}

interface CheckinTemplateSettingsModalProps {
    /** The template being configured; `null` closes the modal. */
    template: CheckinTemplate | null
    onClose: () => void
    /** Persist a new mode or stored time. Rejecting rolls the control back. */
    onUpdate: (template: CheckinTemplate, update: CheckinTemplateUpdate) => Promise<void>
    /** Hand the template to the page's delete confirmation. */
    onDelete: (template: CheckinTemplate) => void
}

const CheckinTemplateSettingsModal: React.FC<CheckinTemplateSettingsModalProps> = ({
    template,
    onClose,
    onUpdate,
    onDelete,
}) => {
    const [busy, setBusy] = useState(false)

    // The picker shows its own copy of the stored time, so a typed time reads
    // back the moment it is typed rather than after the round trip — a time
    // input that lagged its own keystrokes would fight the typing. The saved
    // row replaces it when it arrives, and a failed save puts the stored one
    // back.
    const [draftTime, setDraftTime] = useState<{
        appliesAt: string | null
        precision: AppliesPrecision | null
    }>({ appliesAt: null, precision: null })
    useEffect(() => {
        if (template) {
            setDraftTime({ appliesAt: template.applies_at, precision: template.applies_precision })
        }
    }, [template?.id, template?.applies_at, template?.applies_precision])

    // The clock shown under "use current time", read once per opening rather
    // than on every render: the point of the line is *roughly* what a tap now
    // would write, and a value that changed as the radios were clicked would
    // read as a display that could not sit still. Keyed on the template's id,
    // which is also what makes it a fresh reading each time the modal opens.
    const [openedAt, setOpenedAt] = useState<Date>(() => new Date())
    useEffect(() => {
        if (template) setOpenedAt(new Date())
    }, [template?.id])

    /** The second line under an option: the time that mode would give. */
    const detailFor = (mode: CheckinTemplateTimeMode): string | null => {
        if (!template) return null
        if (mode === 'same') {
            return (
                formatTimeWithPrecision(template.applies_at, template.applies_precision) ??
                NO_STORED_TIME
            )
        }
        if (mode === 'current') {
            const nowAt = minutesToAppliesAt(openedAt.getHours() * 60 + openedAt.getMinutes())
            return formatTimeWithPrecision(nowAt, 'exact')
        }
        return null
    }

    const handlePick = useCallback(
        async (mode: CheckinTemplateTimeMode) => {
            if (!template || busy || mode === template.time_mode) return
            setBusy(true)
            try {
                await onUpdate(template, { time_mode: mode })
            } catch {
                // The owner reported it; the radio reads from `template`,
                // which was never patched, so it is already back.
            } finally {
                setBusy(false)
            }
        },
        [busy, onUpdate, template],
    )

    // Not gated on `busy`: each change is a complete value, and the draft
    // already shows the newest one, so a second edit before the first lands
    // is just the later write.
    const handleTimeChange = useCallback(
        async (appliesAt: string | null, precision: AppliesPrecision) => {
            if (!template) return
            setDraftTime({ appliesAt, precision })
            try {
                await onUpdate(
                    template,
                    appliesAt === null
                        ? { applies_at: null }
                        : { applies_at: appliesAt, applies_precision: precision },
                )
            } catch {
                setDraftTime({
                    appliesAt: template.applies_at,
                    precision: template.applies_precision,
                })
            }
        },
        [onUpdate, template],
    )

    // A backdrop click closes only when the press started on the backdrop.
    //
    // This modal is opened by a *hold on a pill*, and on touch the hold is
    // followed by a compatibility mouse click at the same coordinates — which
    // by then is over this backdrop. Without the guard the modal opened and
    // shut again inside 400ms, which on a phone reads as the hold doing
    // nothing at all. A compatibility click carries no pointer events, so
    // requiring a matching `pointerdown` is enough to tell the two apart, and
    // it costs a genuine backdrop dismissal nothing. Same rule as
    // `ConfirmationModal`, which the delete button below opens on top of this.
    const pressStartedOnBackdrop = useRef(false)

    const handleBackdropPointerDown = (event: React.PointerEvent) => {
        pressStartedOnBackdrop.current = event.target === event.currentTarget
    }

    const handleBackdropClick = (event: React.MouseEvent) => {
        const started = pressStartedOnBackdrop.current
        pressStartedOnBackdrop.current = false
        if (started && event.detail !== 0 && event.target === event.currentTarget && !busy) {
            onClose()
        }
    }

    return (
        <AnimatePresence>
            {template && (
                <motion.div
                    className="fixed inset-0 bg-white/75 z-50 flex items-center justify-center p-4"
                    style={{ backdropFilter: 'blur(1px)' }}
                    onPointerDown={handleBackdropPointerDown}
                    onClick={handleBackdropClick}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                >
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Shortcut settings: ${template.label}`}
                        className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl border border-gray-200"
                        initial={{ opacity: 0, scale: 0.95, y: -8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -8 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    >
                        <h3 className="text-lg font-semibold text-gray-900 break-words">
                            {template.label}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1 mb-4">
                            What time do check-ins from this shortcut get?
                        </p>

                        <div className="flex flex-col gap-3">
                            {TIME_MODE_OPTIONS.map(option => {
                                const editsTime = option.mode === 'same' && template.time_mode === 'same'
                                return (
                                    <div key={option.mode}>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="checkin-template-time-mode"
                                                className="h-4 w-4 mt-0.5 shrink-0"
                                                value={option.mode}
                                                checked={template.time_mode === option.mode}
                                                disabled={busy}
                                                onChange={() => { void handlePick(option.mode) }}
                                            />
                                            <span className="min-w-0">
                                                <span className="block text-sm text-gray-900">{option.label}</span>
                                                {!editsTime && detailFor(option.mode) && (
                                                    <span className="block text-sm text-gray-500">
                                                        {detailFor(option.mode)}
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                        {/* Outside the label, so a tap on the picker's
                                            buttons is not also a tap on the radio. */}
                                        {editsTime && (
                                            <CheckinTimePicker
                                                appliesAt={draftTime.appliesAt}
                                                precision={draftTime.precision}
                                                onChange={(appliesAt, precision) => {
                                                    void handleTimeChange(appliesAt, precision)
                                                }}
                                                resetKey={template.id}
                                                ariaSuffix="for this shortcut"
                                                bucketsAlign="left"
                                                className="mt-1 ml-7"
                                            />
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        {/* Delete sits opposite Done rather than beside it: it is
                            the one irreversible thing on this surface, and the
                            confirmation it opens is what makes it safe. */}
                        <div className="flex items-center justify-between mt-6">
                            <motion.button
                                type="button"
                                onClick={() => onDelete(template)}
                                disabled={busy}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                className="px-3 py-2 text-sm rounded-md text-danger hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Delete shortcut
                            </motion.button>
                            <motion.button
                                type="button"
                                onClick={onClose}
                                disabled={busy}
                                whileTap={{ scale: 0.96 }}
                                className="px-4 py-2 text-gray-700 bg-secondary hover:bg-secondary-dark rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Done
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default CheckinTemplateSettingsModal
